// SubPanel-Elect Tenant Management Service
// Comprehensive tenant oversight and management functionality

import { apiClient } from '@/services/shared/apiClient';
import type {
  TenantManagement,
  TenantOverview,
  TenantAnalytics,
  TenantOperations,
  OnboardingOperations,
  SubpanelElectApiResponse,
  SubpanelElectSearchFilters,
  BulkOperation,
} from '@/types/entities/community_subpanel-elect';

export class TenantManagementService {
  /**
   * Get comprehensive tenant management overview
   */
  async getTenantManagement(): Promise<TenantManagement> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<TenantManagement>>(
        '/subpanel-elect/tenants/overview',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch tenant management data:', error);
      return this.getMockTenantManagement();
    }
  }

  /**
   * Get all tenants with filtering and pagination
   */
  async getTenants(
    filters?: SubpanelElectSearchFilters['tenants'],
    pagination?: { page: number; limit: number; sortBy?: string; sortOrder?: 'asc' | 'desc' },
  ): Promise<{ tenants: TenantOverview[]; total: number; totalPages: number }> {
    try {
      const response = await apiClient.get<
        SubpanelElectApiResponse<{
          tenants: TenantOverview[];
          total: number;
          totalPages: number;
        }>
      >('/subpanel-elect/tenants', { ...filters, ...pagination });
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      return {
        tenants: this.getMockTenants(),
        total: 42,
        totalPages: 5,
      };
    }
  }

  /**
   * Get detailed tenant information
   */
  async getTenantDetails(tenantId: string): Promise<TenantOverview> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<TenantOverview>>(
        `/subpanel-elect/tenants/${tenantId}`,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch tenant details:', error);
      return this.getMockTenants()[0];
    }
  }

  /**
   * Update tenant information
   */
  async updateTenant(
    tenantId: string,
    updates: Partial<Pick<TenantOverview, 'name' | 'displayName' | 'status' | 'customDomain'>>,
  ): Promise<TenantOverview> {
    try {
      const response = await apiClient.put<SubpanelElectApiResponse<TenantOverview>>(
        `/subpanel-elect/tenants/${tenantId}`,
        updates,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to update tenant:', error);
      throw error;
    }
  }

  /**
   * Update tenant subscription
   */
  async updateTenantSubscription(
    tenantId: string,
    subscriptionUpdates: {
      tier?: 'basic' | 'premium' | 'enterprise' | 'custom';
      status?: 'active' | 'trialing' | 'past_due' | 'canceled' | 'paused';
      billingCycle?: 'monthly' | 'quarterly' | 'annually';
      customPricing?: boolean;
      monthlyValue?: number;
    },
  ): Promise<TenantOverview> {
    try {
      const response = await apiClient.put<SubpanelElectApiResponse<TenantOverview>>(
        `/subpanel-elect/tenants/${tenantId}/subscription`,
        subscriptionUpdates,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to update tenant subscription:', error);
      throw error;
    }
  }

  /**
   * Suspend/unsuspend tenant
   */
  async toggleTenantSuspension(
    tenantId: string,
    suspend: boolean,
    reason?: string,
  ): Promise<TenantOverview> {
    try {
      const response = await apiClient.put<SubpanelElectApiResponse<TenantOverview>>(
        `/subpanel-elect/tenants/${tenantId}/${suspend ? 'suspend' : 'unsuspend'}`,
        { reason },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to toggle tenant suspension:', error);
      throw error;
    }
  }

  /**
   * Get tenant analytics
   */
  async getTenantAnalytics(timeframe: '30d' | '90d' | '1y' = '30d'): Promise<TenantAnalytics> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<TenantAnalytics>>(
        '/subpanel-elect/tenants/analytics',
        { timeframe },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch tenant analytics:', error);
      return this.getMockTenantAnalytics();
    }
  }

  /**
   * Get tenant operations data
   */
  async getTenantOperations(): Promise<TenantOperations> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<TenantOperations>>(
        '/subpanel-elect/tenants/operations',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch tenant operations:', error);
      return this.getMockTenantOperations();
    }
  }

  /**
   * Get onboarding pipeline
   */
  async getOnboardingPipeline(): Promise<OnboardingOperations> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<OnboardingOperations>>(
        '/subpanel-elect/tenants/onboarding',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch onboarding pipeline:', error);
      return this.getMockOnboardingOperations();
    }
  }

  /**
   * Update onboarding stage for tenant
   */
  async updateOnboardingStage(
    tenantId: string,
    stage: string,
    progress: number,
    notes?: string,
  ): Promise<void> {
    try {
      await apiClient.put(`/subpanel-elect/tenants/${tenantId}/onboarding`, {
        stage,
        progress,
        notes,
      });
    } catch (error) {
      console.error('Failed to update onboarding stage:', error);
      throw error;
    }
  }

  /**
   * Complete onboarding for tenant
   */
  async completeOnboarding(tenantId: string): Promise<void> {
    try {
      await apiClient.put(`/subpanel-elect/tenants/${tenantId}/onboarding/complete`);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      throw error;
    }
  }

  /**
   * Search tenants
   */
  async searchTenants(
    query: string,
    filters?: SubpanelElectSearchFilters['tenants'],
  ): Promise<TenantOverview[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<TenantOverview[]>>(
        '/subpanel-elect/tenants/search',
        { query, ...filters },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to search tenants:', error);
      return this.getMockTenants().filter(
        (tenant) =>
          tenant.name.toLowerCase().includes(query.toLowerCase()) ||
          tenant.displayName.toLowerCase().includes(query.toLowerCase()),
      );
    }
  }

  /**
   * Bulk update tenants
   */
  async bulkUpdateTenants(
    tenantIds: string[],
    updates: {
      status?: TenantOverview['status'];
      tier?: 'basic' | 'premium' | 'enterprise';
      tags?: string[];
    },
  ): Promise<BulkOperation> {
    try {
      const response = await apiClient.post<SubpanelElectApiResponse<BulkOperation>>(
        '/subpanel-elect/tenants/bulk-update',
        { tenantIds, updates },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to bulk update tenants:', error);
      throw error;
    }
  }

  /**
   * Export tenant data
   */
  async exportTenantData(
    format: 'csv' | 'excel' | 'json' = 'excel',
    filters?: SubpanelElectSearchFilters['tenants'],
  ): Promise<Blob> {
    try {
      const response = await fetch('/subpanel-elect/tenants/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ format, filters }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to export tenant data:', error);
      throw error;
    }
  }

  /**
   * Get tenant health check
   */
  async getTenantHealthCheck(tenantId: string): Promise<TenantOverview['health']> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<TenantOverview['health']>>(
        `/subpanel-elect/tenants/${tenantId}/health`,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch tenant health:', error);
      return {
        overall: 'healthy',
        score: 95,
        factors: [],
        lastChecked: new Date().toISOString(),
      };
    }
  }

  /**
   * Send notification to tenant
   */
  async sendTenantNotification(
    tenantId: string,
    notification: {
      type: 'info' | 'warning' | 'error' | 'maintenance';
      title: string;
      message: string;
      channels: ('email' | 'dashboard' | 'sms')[];
      urgency: 'low' | 'medium' | 'high';
    },
  ): Promise<void> {
    try {
      await apiClient.post(`/subpanel-elect/tenants/${tenantId}/notifications`, notification);
    } catch (error) {
      console.error('Failed to send tenant notification:', error);
      throw error;
    }
  }

  // Mock data methods for development
  private getMockTenantManagement(): TenantManagement {
    return {
      tenants: this.getMockTenants(),
      analytics: this.getMockTenantAnalytics(),
      operations: this.getMockTenantOperations(),
      compliance: {
        requirements: [],
        audits: [],
        violations: [],
        certifications: [],
      },
    };
  }

  private getMockTenants(): TenantOverview[] {
    return [
      {
        id: '1',
        name: 'sunset-gardens',
        displayName: 'Sunset Gardens HOA',
        subdomain: 'sunset-gardens',
        customDomain: 'community.sunsetgardens.com',
        status: 'active',
        health: {
          overall: 'healthy',
          score: 95,
          factors: [
            {
              name: 'User Engagement',
              status: 'good',
              value: 87,
              threshold: 70,
              impact: 'medium',
              description: 'Active user participation',
            },
            {
              name: 'System Performance',
              status: 'good',
              value: 98,
              threshold: 90,
              impact: 'high',
              description: 'Excellent response times',
            },
            {
              name: 'Financial Health',
              status: 'good',
              value: 92,
              threshold: 80,
              impact: 'high',
              description: 'On-time payments',
            },
          ],
          lastChecked: new Date().toISOString(),
        },
        subscription: {
          id: 'sub_1',
          tier: 'premium',
          plan: {
            id: 'premium_monthly',
            name: 'Premium Monthly',
            tier: 'premium',
            price: 99,
            currency: 'USD',
            features: [
              {
                id: 'governance',
                name: 'Advanced Governance',
                description: 'Voting and proposal system',
                category: 'governance',
                included: true,
              },
              {
                id: 'finance',
                name: 'Financial Management',
                description: 'Budget and expense tracking',
                category: 'finance',
                included: true,
                limit: 10,
                usage: 3,
              },
            ],
            limits: {
              maxUsers: 500,
              maxStorage: 50,
              maxDomains: 1,
              maxIntegrations: 10,
              maxCustomFields: 25,
              apiCallsPerMonth: 10000,
              supportLevel: 'priority',
            },
            addOns: [],
          },
          status: 'active',
          billingCycle: 'monthly',
          currentPeriodStart: '2024-12-01T00:00:00Z',
          currentPeriodEnd: '2025-01-01T00:00:00Z',
          nextBillingDate: '2025-01-01T00:00:00Z',
          isTrialing: false,
          monthlyValue: 99,
          annualValue: 1188,
          totalRevenue: 5940,
          outstandingBalance: 0,
          contractStart: '2024-01-15T00:00:00Z',
          autoRenewal: true,
          customPricing: false,
        },
        metrics: {
          users: {
            total: 245,
            active: 189,
            activeLastWeek: 165,
            activeLastMonth: 201,
            newThisMonth: 12,
            churnedThisMonth: 3,
            growthRate: 5.2,
            byRole: [
              { role: 'resident', count: 220, active: 168 },
              { role: 'manager', count: 15, active: 14 },
              { role: 'admin', count: 10, active: 7 },
            ],
            engagement: {
              dailyActive: 89,
              weeklyActive: 165,
              monthlyActive: 201,
              averageSessionDuration: 18.5,
              averageSessionsPerUser: 2.3,
            },
          },
          usage: {
            storage: { used: 28.5, limit: 50, utilization: 57.0 },
            bandwidth: { used: 145.2, limit: 500, utilization: 29.0 },
            apiCalls: { thisMonth: 4250, limit: 10000, utilization: 42.5, averagePerDay: 137 },
            features: [
              {
                featureId: 'governance',
                featureName: 'Governance',
                usage: 85,
                adoption: 78,
                lastUsed: '2024-12-15T14:30:00Z',
              },
              {
                featureId: 'finance',
                featureName: 'Finance',
                usage: 42,
                adoption: 58,
                lastUsed: '2024-12-15T09:15:00Z',
              },
            ],
            modules: [
              {
                moduleId: 'governance',
                moduleName: 'Governance',
                enabled: true,
                adoption: 78,
                usage: 85,
              },
              {
                moduleId: 'finance',
                moduleName: 'Finance',
                enabled: true,
                adoption: 58,
                usage: 42,
              },
            ],
          },
          engagement: {
            overallScore: 8.7,
            loginFrequency: 85.2,
            featureAdoption: 72.5,
            dataCompleteness: 89.3,
            socialActivity: 65.8,
            trends: [
              { metric: 'Login Frequency', trend: 'increasing', change: 5.2, period: '30d' },
              { metric: 'Feature Adoption', trend: 'stable', change: 1.1, period: '30d' },
            ],
            stickiness: {
              dau_mau: 44.3,
              wau_mau: 82.1,
              retention: [
                { cohort: '2024-11', size: 12, day1: 91.7, day7: 83.3, day30: 75.0, day90: 66.7 },
              ],
            },
          },
          financial: {
            revenue: { monthly: 99, annual: 1188, lifetime: 5940, growth: 12.3 },
            costs: { acquisition: 85, support: 25, infrastructure: 18, total: 128 },
            profitability: { margin: 77.3, contribution: 76.5, paybackPeriod: 5.1 },
            billing: { onTime: 100, late: 0, failed: 0, disputed: 0 },
          },
          support: {
            tickets: { total: 8, open: 1, resolved: 7, escalated: 0 },
            performance: {
              averageResponseTime: 2.5,
              averageResolutionTime: 4.2,
              firstContactResolution: 87.5,
              satisfaction: 9.1,
            },
            categories: [
              { category: 'Technical', count: 4, avgResolutionTime: 3.8 },
              { category: 'Billing', count: 2, avgResolutionTime: 1.5 },
              { category: 'Feature Request', count: 2, avgResolutionTime: 7.2 },
            ],
          },
        },
        primaryContact: {
          id: 'contact_1',
          firstName: 'Sarah',
          lastName: 'Johnson',
          email: 'sarah.johnson@sunsetgardens.com',
          phone: '+1-555-0123',
          role: 'Community Manager',
          department: 'Operations',
          timezone: 'America/Los_Angeles',
          preferredContact: 'email',
          accountManager: 'John Smith',
          lastContactDate: '2024-12-10T00:00:00Z',
          nextContactDate: '2024-12-20T00:00:00Z',
          satisfactionScore: 9.2,
          communicationPreferences: ['email', 'monthly_review'],
        },
        settings: {
          general: {
            timezone: 'America/Los_Angeles',
            language: 'en',
            dateFormat: 'MM/DD/YYYY',
            currency: 'USD',
          },
          branding: {
            primaryColor: '#16a34a',
            accentColor: '#065f46',
            logo: '/logos/sunset-gardens.png',
          },
          security: {
            passwordPolicy: {
              minLength: 8,
              requireUppercase: true,
              requireLowercase: true,
              requireNumbers: true,
              requireSymbols: false,
              maxAge: 90,
              preventReuse: 5,
            },
            sessionTimeout: 60,
            ipWhitelist: [],
            ssoEnabled: false,
            twoFactorRequired: false,
          },
          features: {
            enabled: ['governance', 'finance', 'communication'],
            disabled: ['marketplace'],
            customizations: {},
          },
          integrations: { active: [], available: ['quickbooks', 'mailchimp', 'slack'] },
          notifications: {
            email: true,
            sms: false,
            webhook: false,
            preferences: [
              { type: 'billing', enabled: true, channels: ['email'], frequency: 'immediate' },
              { type: 'security', enabled: true, channels: ['email'], frequency: 'immediate' },
            ],
          },
        },
        createdAt: '2024-01-15T10:00:00Z',
        activatedAt: '2024-01-16T14:30:00Z',
        lastActivityAt: '2024-12-15T16:45:00Z',
        updatedAt: '2024-12-15T14:30:00Z',
      },
    ];
  }

  private getMockTenantAnalytics(): TenantAnalytics {
    return {
      summary: {
        total: 42,
        active: 38,
        trialing: 6,
        churned: 4,
        suspended: 0,
        byTier: [
          { tier: 'basic', count: 18, percentage: 42.9, revenue: 89100 },
          { tier: 'premium', count: 19, percentage: 45.2, revenue: 224820 },
          { tier: 'enterprise', count: 5, percentage: 11.9, revenue: 229440 },
        ],
        byRegion: [
          { region: 'North America', count: 25, percentage: 59.5 },
          { region: 'Europe', count: 12, percentage: 28.6 },
          { region: 'Asia Pacific', count: 5, percentage: 11.9 },
        ],
        growth: { newThisMonth: 7, churnedThisMonth: 2, netGrowth: 5, growthRate: 13.5 },
      },
      distribution: {
        bySize: [
          { range: '1-50 users', count: 18, percentage: 42.9 },
          { range: '51-200 users', count: 15, percentage: 35.7 },
          { range: '201-500 users', count: 7, percentage: 16.7 },
          { range: '500+ users', count: 2, percentage: 4.8 },
        ],
        byRevenue: [
          { range: '$0-$1K', count: 8, percentage: 19.0 },
          { range: '$1K-$5K', count: 18, percentage: 42.9 },
          { range: '$5K-$10K', count: 12, percentage: 28.6 },
          { range: '$10K+', count: 4, percentage: 9.5 },
        ],
        byAge: [
          { range: '0-3 months', count: 8, percentage: 19.0 },
          { range: '3-12 months', count: 15, percentage: 35.7 },
          { range: '1-2 years', count: 12, percentage: 28.6 },
          { range: '2+ years', count: 7, percentage: 16.7 },
        ],
        byIndustry: [
          { industry: 'Residential HOA', count: 25, percentage: 59.5 },
          { industry: 'Commercial Property', count: 8, percentage: 19.0 },
          { industry: 'Mixed Use', count: 6, percentage: 14.3 },
          { industry: 'Other', count: 3, percentage: 7.1 },
        ],
      },
      trends: {
        acquisition: [
          { period: '2024-11', value: 5, change: 25.0, trend: 'up' },
          { period: '2024-12', value: 7, change: 40.0, trend: 'up' },
        ],
        churn: [
          { period: '2024-11', value: 1, change: -50.0, trend: 'down' },
          { period: '2024-12', value: 2, change: 100.0, trend: 'up' },
        ],
        expansion: [
          { period: '2024-11', value: 3, change: 50.0, trend: 'up' },
          { period: '2024-12', value: 4, change: 33.3, trend: 'up' },
        ],
        engagement: [
          { period: '2024-11', value: 78.5, change: 2.1, trend: 'up' },
          { period: '2024-12', value: 82.7, change: 5.4, trend: 'up' },
        ],
      },
      cohorts: {
        retention: [
          { cohort: '2024-Q1', size: 12, retentionRates: [100, 91.7, 83.3, 83.3] },
          { cohort: '2024-Q2', size: 15, retentionRates: [100, 93.3, 86.7, 80.0] },
          { cohort: '2024-Q3', size: 18, retentionRates: [100, 94.4, 88.9] },
        ],
        revenue: [
          { cohort: '2024-Q1', size: 12, revenuePerPeriod: [1188, 1250, 1285, 1420] },
          { cohort: '2024-Q2', size: 15, revenuePerPeriod: [1485, 1620, 1758] },
        ],
      },
      segments: {
        behavioral: [
          {
            id: 'high_engagement',
            name: 'High Engagement',
            description: 'Tenants with high user engagement and feature adoption',
            criteria: { engagement: '>80', featureAdoption: '>70' },
            size: 12,
            characteristics: [
              'Daily active users',
              'High feature usage',
              'Strong governance participation',
            ],
            averageRevenue: 15420,
            retentionRate: 98.5,
            engagementScore: 87.5,
          },
        ],
        demographic: [
          {
            id: 'large_hoa',
            name: 'Large HOAs',
            description: 'HOAs with 200+ units',
            criteria: { users: '>200', type: 'HOA' },
            size: 8,
            characteristics: [
              'Complex governance needs',
              'Multiple committees',
              'High transaction volume',
            ],
            averageRevenue: 28500,
            retentionRate: 96.2,
            engagementScore: 82.1,
          },
        ],
        usage: [
          {
            id: 'power_users',
            name: 'Power Users',
            description: 'Tenants utilizing multiple advanced features',
            criteria: { features: '>5', usage: '>80' },
            size: 15,
            characteristics: [
              'Multi-module usage',
              'Custom integrations',
              'Advanced configurations',
            ],
            averageRevenue: 18750,
            retentionRate: 97.8,
            engagementScore: 89.2,
          },
        ],
        value: [
          {
            id: 'high_value',
            name: 'High Value',
            description: 'Tenants contributing most to revenue',
            criteria: { revenue: '>10000' },
            size: 8,
            characteristics: ['Enterprise tier', 'Custom pricing', 'Dedicated support'],
            averageRevenue: 28700,
            retentionRate: 100.0,
            engagementScore: 85.5,
          },
        ],
      },
    };
  }

  private getMockTenantOperations(): TenantOperations {
    return {
      onboarding: this.getMockOnboardingOperations(),
      lifecycle: {
        stages: [
          {
            name: 'Trial',
            description: 'Evaluating the platform',
            criteria: { status: 'trialing' },
            actions: ['Send welcome sequence', 'Schedule demo', 'Provide resources'],
            duration: 14,
            tenantCount: 6,
          },
          {
            name: 'New Customer',
            description: 'Recently converted customers',
            criteria: { status: 'active', age: '<90' },
            actions: ['Complete onboarding', 'First success milestone', 'Regular check-ins'],
            duration: 90,
            tenantCount: 12,
          },
          {
            name: 'Established',
            description: 'Stable, long-term customers',
            criteria: { status: 'active', age: '>90' },
            actions: ['Quarterly reviews', 'Expansion opportunities', 'Success metrics'],
            duration: 365,
            tenantCount: 24,
          },
        ],
        transitions: [
          {
            from: 'Trial',
            to: 'New Customer',
            triggers: ['Payment completion', 'Contract signed'],
            automaticActions: ['Send welcome email', 'Assign account manager'],
            approvalRequired: false,
          },
        ],
        automation: [
          {
            id: 'trial_conversion',
            name: 'Trial Conversion Follow-up',
            stage: 'Trial',
            triggers: ['7 days remaining', 'Low engagement'],
            actions: ['Send conversion email', 'Schedule demo', 'Offer discount'],
            enabled: true,
          },
        ],
      },
      support: {
        escalations: [
          {
            tenantId: '1',
            tenantName: 'Sunset Gardens HOA',
            issueType: 'Technical',
            severity: 'medium',
            description: 'Performance issues during peak usage',
            assignedTo: 'Support Team Lead',
            status: 'in_progress',
            createdAt: '2024-12-14T09:30:00Z',
            lastUpdate: '2024-12-15T14:15:00Z',
          },
        ],
        knowledge: [
          {
            id: 'kb_001',
            title: 'Setting up Community Governance',
            category: 'Governance',
            content: 'Step-by-step guide for configuring governance settings...',
            tags: ['governance', 'setup', 'voting'],
            usage: 245,
            rating: 4.8,
            lastUpdated: '2024-12-01T00:00:00Z',
          },
        ],
        automation: [
          {
            id: 'auto_response',
            name: 'Automated First Response',
            type: 'chatbot',
            triggers: ['New ticket created'],
            actions: ['Send acknowledgment', 'Gather initial info', 'Route to appropriate team'],
            successRate: 85.2,
          },
        ],
      },
      compliance: {
        requirements: [],
        audits: [],
        violations: [],
        certifications: [],
      },
    };
  }

  private getMockOnboardingOperations(): OnboardingOperations {
    return {
      pipeline: [
        {
          tenantId: '2',
          tenantName: 'Oak Valley Community',
          stage: 'Configuration',
          progress: 65,
          assignedTo: 'Implementation Team',
          startDate: '2024-12-10T00:00:00Z',
          expectedCompletion: '2024-12-25T00:00:00Z',
          blockers: ['Awaiting custom branding assets'],
          notes: ['Customer responsive', 'Making good progress'],
        },
      ],
      templates: [
        {
          id: 'premium_onboarding',
          name: 'Premium Tier Onboarding',
          description: 'Comprehensive onboarding for premium customers',
          tier: 'premium',
          steps: [
            {
              id: 'setup',
              name: 'Initial Setup',
              description: 'Configure basic tenant settings',
              type: 'manual',
              duration: 2,
              dependencies: [],
              resources: ['Setup checklist', 'Configuration guide'],
              completed: true,
            },
            {
              id: 'branding',
              name: 'Branding Configuration',
              description: 'Apply custom branding and themes',
              type: 'manual',
              duration: 1,
              dependencies: ['setup'],
              resources: ['Branding guide', 'Asset templates'],
              completed: false,
            },
          ],
          estimatedDuration: 10,
          resources: ['Onboarding checklist', 'Premium feature guide', 'Best practices document'],
        },
      ],
      automation: [
        {
          id: 'welcome_sequence',
          name: 'Welcome Email Sequence',
          trigger: 'Tenant created',
          actions: ['Send welcome email', 'Schedule kickoff call', 'Provide resources'],
          enabled: true,
          successRate: 92.5,
        },
      ],
      metrics: {
        averageDuration: 12,
        completionRate: 88.5,
        timeToValue: 18,
        customerSatisfaction: 4.6,
        dropoffPoints: [
          { step: 'Initial Setup', dropoffRate: 5.2 },
          { step: 'User Training', dropoffRate: 8.1 },
        ],
      },
    };
  }
}

export const tenantManagementService = new TenantManagementService();
